import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Observable, Subscription } from 'rxjs';
import { map, startWith, tap, withLatestFrom } from 'rxjs/operators';
import { ActionList } from '../../action-lists/action-list';
import { ActionListsApiService } from '../action-lists.api.service';
import { ActionListsStoreService } from '../action-lists.store.service';

enum SelectedTab {
  add = 0,
  create = 1,
}

@Component({
  templateUrl: './add-to-list-dialog.component.html',
  styleUrls: ['./add-to-list-dialog.component.scss'],
})
export class AddToListDialogComponent implements OnInit, OnDestroy {
  myControl = new UntypedFormControl();
  @Output() selectedActionList: EventEmitter<ActionList> = new EventEmitter<ActionList>();

  selectedList: ActionList;
  loading = false;
  form: UntypedFormGroup;
  filteredLists$: Observable<ActionList[]>;
  CREATE_NEW_LIST = 'createNewList';
  selectedIndex = 0;
  title: string;
  private defaultListName: string;
  private subscriptions: Subscription[] = [];

  constructor(
    public dialogRef: MatDialogRef<AddToListDialogComponent>,
    private snackbarService: SnackbarService,
    public actionListsApiService: ActionListsApiService,
    public actionListsStoreService: ActionListsStoreService,
    private formBuilder: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      title: string;
      defaultListName: string;
    },
  ) {
    this.title = data.title;
    this.defaultListName = data.defaultListName;
  }

  ngOnInit(): void {
    this.actionListsStoreService.initializeStore();
    const listName = this.defaultListName ? this.defaultListName : '';
    if (listName) {
      this.selectedIndex = 1;
    }
    this.form = this.formBuilder.group({
      createNewList: [listName],
      existingLists: [''],
    });

    this.filteredLists$ = this.myControl.valueChanges.pipe(
      startWith<string | ActionList>(''),
      map((value) => (typeof value === 'string' ? value : value.name)),
      withLatestFrom(this.actionListsStoreService.actionLists$),
      map(([name, actionLists]) => {
        if (name) {
          return actionLists.filter((list) => list.name.toLowerCase().includes(name.toLowerCase()));
        }
        return actionLists.slice();
      }),
    );
  }

  displayFn(actionList?: ActionList): string | undefined {
    return actionList ? actionList.name : undefined;
  }

  keyPressed(event: KeyboardEvent): void {
    if (event.charCode === 13) {
      // Enter pressed
      this.addToList();
    }
  }

  get submitText(): string {
    if (this.selectedIndex === SelectedTab.create) {
      return 'Create and Add';
    } else {
      return 'Add';
    }
  }

  emitOutputAndCloseDialog(): void {
    this.selectedActionList.emit(this.selectedList);
    this.dialogRef.close();
  }

  addToList(): void {
    if (this.form.controls[this.CREATE_NEW_LIST].value !== '' && this.selectedIndex === 1) {
      this.loading = true;
      this.subscriptions.push(
        this.actionListsApiService
          .createActionList(this.form.controls[this.CREATE_NEW_LIST].value)
          .pipe(
            tap(
              (actionList) => {
                this.selectedList = actionList;
                this.emitOutputAndCloseDialog();
                this.loading = false;
              },
              () => {
                this.snackbarService.errorSnack('Error creating a list. Please try again.');
                this.loading = false;
              },
            ),
          )
          .subscribe(),
      );
    } else {
      this.selectedList = this.myControl.value;
      this.emitOutputAndCloseDialog();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.map((s) => s.unsubscribe());
  }
}
